<template>
	<div class="cog-stripe-payment">
		<stripe-card-placeholder v-if="showLoader" :mobile="mobile" />
		<stripe-card
			ref="card"
			:publishable-key="apiKey"
			:connected-account-id="session.stripeConnectedAccountId"
			:locale="$culture.name"
			:input-styles="inputStyles"
			:amount="orderAmountCents"
			:save-card="saveCard"
			:payment-required="paymentRequired"
			:currency="currency"
			:billing-details="billingDetails"
			:use-wallets="useWallets"
			@ready="handlePaymentElementReady"
			@load-error="handleLoadError"
			@load-error-cleared="handleLoadErrorCleared"
			@focus="handleFocus"
			@blur="handleBlur"
			@change="handleChange"
		>
			<div id="cog-payment-element" class="cog-payment__frame cog-field">
			</div>
		</stripe-card>
	</div>
</template>

<script>
	import StripeCard, { PaymentMethodType } from './StripeCard';
	import StripeCardPlaceholder from './StripeCardPlaceholder.vue';
	import { FriendlyError } from '../../framework/logging/error';
	import { removeEmptyProps } from 'src/util/helpers';
	import { toCents } from 'src/util/currency';
	import OrderBuilder from '../../framework/payment/order-builder';
	import { isSavedCardToken } from '../../framework/payment/saved-card-token';
	import { PaymentTokenStatus } from '@cognitoforms/types/server-types/payment/model/payment-token-status';

	export default {
		name: 'CStripePayment',
		components: {
			StripeCard,
			StripeCardPlaceholder
		},
		inject: {
			$resource: {},
			log: {},
			session: {},
			$culture: {},
			registerNewCardComponent: { default: null }
		},
		props: {
			entry: { type: Object, required: true },
			orderInfo: { type: Object, default: null },
			apiKey: { type: String, required: true },
			currencyCode: { type: String, default: 'USD' },
			inputStyles: { type: Object, default: null },
			saveCard: { type: Boolean, default: false },
			paymentRequired: { type: Boolean, default: false },
			mobile: { type: Boolean, default: null },
			useWallets: { type: Boolean, default: true }
		},
		data: function() {
			return {
				showLoader: true,
				creatingTokenPromise: null,
				creatingTokenRevision: 0,
				cardRevision: 0,
				paymentTokenRequest: 0,
				temporaryPaymentToken: null,
				cardPaymentToken: null,
				loadError: null,
				cardTouchedSinceToken: false
			};
		},
		computed: {
			loadErrorMessage() {
				if (!this.loadError)
					return '';

				// Stripe localizes its own error messages using the locale passed to elements()
				return this.loadError.message || this.$resource('order-exception-default-message');
			},
			order() {
				return this.entry.Order;
			},
			orderAmount() {
				return this.order.OrderAmount;
			},
			paymentToken() {
				return this.entry.Entry.PaymentToken;
			},
			orderAmountCents() {
				if (this.orderAmount)
					return toCents(this.orderAmount, {
						NumberOfDecimals: this.$culture.numberFormat.CurrencyDecimalDigits
					});
				else
					return 0;
			},
			currency() {
				return this.currencyCode ? this.currencyCode.toLowerCase() : null;
			},
			billingDetails() {
				if (!this.orderInfo)
					return null;

				/**
				 * @type {StripeCard.BillingDetails}
				 */
				const billingDetails = {};

				if (this.orderInfo.Name && (this.orderInfo.Name.First || this.orderInfo.Name.Last)) {
					billingDetails.name = this.orderInfo.Name.First + ' ' + this.orderInfo.Name.Last;
				}
				if (this.orderInfo.Address) {
					billingDetails.address = {
						line1: this.orderInfo.Address.Line1,
						line2: this.orderInfo.Address.Line2,
						city: this.orderInfo.Address.City,
						state: this.orderInfo.Address.State,
						postal_code: this.orderInfo.Address.PostalCode,
						country: this.orderInfo.Address.CountryCode
					};
				}

				billingDetails.email = this.orderInfo.Email;
				billingDetails.phone = this.orderInfo.Phone;

				return removeEmptyProps(billingDetails);
			}
		},
		mounted() {
			if (this.registerNewCardComponent)
				this.registerNewCardComponent(this);
		},
		beforeDestroy() {
			if (this.registerNewCardComponent)
				this.registerNewCardComponent(null);
		},
		destroyed() {
			// Don't leave the form blocked by a load error once the payment component is gone
			if (this.loadError)
				this.handleLoadErrorCleared();

			// The 'temporaryPaymentToken' is a payment token created internally by the payment component as the
			// credit card fields are interacted with (as soon as they are all filled out). If that is the current
			// payment token, then it should be cleared out since the token was not created in the context of submitting
			// the form, ex: the payment component is being destroyed due to a change that causes payment not to be required.
			if (this.temporaryPaymentToken && this.entry.Entry.PaymentToken === this.temporaryPaymentToken)
				this.entry.Entry.PaymentToken = null;
		},
		methods: {
			/**
			 * Create a payment intent when the form is submitted,
			 * or when multi-fees (ex: domestic vs. international) can be calculated.
			 * @returns { Promise<import('../../framework/payment/payment-types').PaymentTokenInfo> } - The token that was created
			 */
			createToken() {
				// An in-flight request may be reused only while the card it read is still the card in the
				// field, so a submit landing mid-blur costs no second round-trip. Once the card changes,
				// its result is stale and a fresh request is queued behind it instead.
				if (this.creatingTokenPromise && this.creatingTokenRevision === this.cardRevision)
					return this.creatingTokenPromise;

				const pending = this.creatingTokenPromise ? this.creatingTokenPromise.catch(() => { }) : Promise.resolve();
				const creating = pending.then(() => this.createTokenInternal());

				this.creatingTokenPromise = creating;
				this.creatingTokenRevision = this.cardRevision;
				creating.catch(() => { }).then(() => {
					if (this.creatingTokenPromise === creating)
						this.creatingTokenPromise = null;
				});

				return creating;
			},

			async createTokenInternal() {
				if (this.loadError)
					throw new Error(this.loadErrorMessage);

				let paymentMethod = null;
				try {
					/**
					 * @type {StripeCard}
					 */
					const stripeCard = this.$refs.card;
					if (!stripeCard) {
						throw new Error('Couldn\'t find Stripe payment form.');
					}

					paymentMethod = await stripeCard.capturePaymentMethod().then(response => {
						if (!response.error) {
							// Return the payment method from the response
							return response.paymentMethod;
						}
						else {
							let message = '';
							if (response.error) {
								if (response.error.code) {
									try {
										message = this.$resource(response.error.code);
									}
									catch {
										// Ignore error since resource will not be defined for all Stripe error codes
									}
								}
								// Stripe rejects the amount itself rather than the card, and localizes that message for us
								if (!message && response.error.code === 'amount_too_small')
									message = response.error.message;
							}
							if (!message)
								message = this.$resource('credit-card-invalid');
							throw new Error(message);
						}
					});
				}
				catch (e) {
					this.log.error(e, { method: 'StripePayment.createToken' });

					if (e.name && e.name === 'IntegrationError') {
						// Don't show integration errors to the end-user
						throw new FriendlyError('An error occurred');
					}
					// If it's an error
					else if (Object.prototype.toString.call(e) === '[object Error]' || (typeof e.message === 'string' && typeof e.stack === 'string')) {
						e.log = false;
						throw e;
					}
					else {
						let message = 'Unknown error';
						if (e) {
							if (e.message)
								message = e.message;
							else {
								// Don't use toString version of the object if it is simply the type representation
								const errorToString = e.toString();
								if (errorToString !== Object.prototype.toString.call(e))
									message = errorToString;
							}
						}
						throw new FriendlyError(message);
					}
				}

				return {
					Type: 'PaymentMethod',
					Token: paymentMethod.id,
					DateCreated: new Date(),
					Status: this.paymentRequired ? 'PendingPayment' : this.saveCard ? 'PendingSaveCard' : null,
					SaveForFutureUse: this.saveCard,
					IsLive: paymentMethod.livemode,
					Card: {
						Country: paymentMethod.card.country,
						Brand: paymentMethod.card.brand,
						Last4: paymentMethod.card.last4
					}
				};
			},

			handlePaymentElementReady(e) {
				this.loadError = null;
				this.$emit('ready');
				this.showLoader = false;
			},

			handleLoadError(e) {
				this.loadError = (e && e.error) || {};
				this.showLoader = false;
				this.$emit('load-error', this.loadErrorMessage);
			},

			handleLoadErrorCleared() {
				this.loadError = null;
				this.showLoader = true;
				this.$emit('load-error-cleared');
			},

			handleFocus(event) {
				// Arming on entry rather than on change is deliberate: the payment element only emits
				// change when the payload it carries changes, so editing a complete card into another
				// complete card emits nothing at all and leaves nothing else to re-tokenize on.
				this.cardTouchedSinceToken = true;

				/**
				 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
				 */
				const focusEvent = {
					type: 'focus',
					sender: this,
					originalEvent: event,
					originalSender: this.$refs.card
				};
				this.$emit('focus', focusEvent);
			},

			async handleBlur(event) {
				/**
				 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
				 */
				const blurEvent = {
					type: 'blur',
					sender: this,
					originalEvent: event,
					originalSender: this.$refs.card
				};
				this.$emit('blur', blurEvent);

				// The payment element's change event payload carries only completeness, not the card
				// itself, so replacing one complete card with another emits nothing and the fee would
				// keep resolving against the previous card's country. Re-tokenize once on the way out.
				const card = this.$refs.card;
				if (this.cardTouchedSinceToken && card && card.paymentMethodType === PaymentMethodType.Card) {
					this.cardTouchedSinceToken = false;
					await this.updatePaymentToken();
				}
			},

			async handleChange(event) {
				// Clear out a cached payment method response if any of the credit card fields change
				this.paymentMethodResponse = null;
				this.cardTouchedSinceToken = true;
				this.cardRevision++;

				await this.updatePaymentToken();
			},

			/**
			 * Resolves the payment token from the card currently entered, so that the country-dependent
			 * processing fee shown to the payer reflects it. Clears the token if the card is incomplete.
			 */
			isSameCard(existingToken, token) {
				const existingCard = existingToken && existingToken.Card;
				const card = token && token.Card;

				return !!existingCard && !!card
					&& existingCard.Country === card.Country
					&& existingCard.Brand === card.Brand
					&& existingCard.Last4 === card.Last4;
			},

			async updatePaymentToken() {
				const formEntry = this.entry;
				const currentToken = formEntry.Entry.PaymentToken;
				const request = ++this.paymentTokenRequest;

				// Check to see if all payment fields on the form have been entered
				const elementStatus = this.$refs.card ? this.$refs.card.elementStatus : null;
				const isComplete = !!elementStatus && elementStatus.isComplete && !elementStatus.isEmpty;

				// Create a token if the card information is complete
				if (isComplete) {
					const orderBuilder = new OrderBuilder(this.entry);
					if (orderBuilder.processingFees && orderBuilder.processingFees.length > 1) {
						try {
							const token = await this.createToken();

							// Only the most recent request may apply its token, so a slower earlier one
							// cannot overwrite the fee resolved from the card currently entered.
							if (request !== this.paymentTokenRequest)
								return;

							this.cardTouchedSinceToken = false;
							this.cardPaymentToken = token;

							if (isSavedCardToken(formEntry.Entry.PaymentToken))
								return;

							// Entering and leaving the card without editing it re-tokenizes to the same
							// card, so leave the model alone rather than rebuilding the order for it.
							if (this.isSameCard(formEntry.Entry.PaymentToken, token))
								return;

							this.applyCardPaymentToken();
						}
						catch (error) {
							this.log.error(error);
							if (request !== this.paymentTokenRequest)
								return;

							this.cardPaymentToken = null;

							// If failed to create a token, then clear out an existing one, unless it is a saved card the card field never produced
							if (formEntry.Entry.PaymentToken && !isSavedCardToken(formEntry.Entry.PaymentToken))
								formEntry.Entry.PaymentToken = null;
						}
					}
				}
				// Otherwise, clear out the token if one existed previously, unless it is a saved card the card field never produced
				else {
					this.cardPaymentToken = null;

					if (currentToken && !isSavedCardToken(currentToken)) {
						formEntry.Entry.PaymentToken = null;
					}
				}
			},

			applyCardPaymentToken() {
				this.entry.Entry.update('PaymentToken', this.cardPaymentToken);
				this.temporaryPaymentToken = this.entry.Entry.PaymentToken;
			},

			restorePaymentToken() {
				if (this.cardPaymentToken && !this.entry.Entry.PaymentToken)
					this.applyCardPaymentToken();
			},

			/**
 			* Moves focus into the card inputs. Called when something that had focus is being replaced by this
 			* component — see `focusNewCard` in SavedCardPayment.vue, which runs after the last saved card is deleted.
 			*/
			focus() {
				this.$refs.card && this.$refs.card.focus();
			},

			/**
			 * Attempts to authenticate the current payment or card using the
			 * payment token provided by the server
			 */
			async authenticate() {
				if (!this.paymentToken)
					return;

				if (!this.paymentToken.ClientSecret) {
					this.$emit('authentication-complete', { success: false });
					return;
				}

				const clientSecret = this.paymentToken.ClientSecret;
				const component = this.$refs.card;

				if (this.paymentToken.Type === 'SetupIntent') {
					const result = await component.authenticateCard(clientSecret);

					this.authenticationComplete({
						success: !result.error,
						status: result.error ? PaymentTokenStatus.AuthenticationFailed : PaymentTokenStatus.AuthenticationSucceeded,
						token: result.setupIntent ? result.setupIntent.id : null,
						type: 'SetupIntent'
					});
				}
				else {
					const result = await component.authenticatePayment(clientSecret);
					console.log(result);

					this.authenticationComplete({
						success: !result.error,
						status: result.error ? PaymentTokenStatus.AuthenticationFailed : result.paymentIntent.status === 'succeeded' ? PaymentTokenStatus.Confirmed : PaymentTokenStatus.AuthenticationSucceeded,
						token: result.paymentIntent ? result.paymentIntent.id : null,
						type: 'PaymentIntent'
					});
				}
			},

			/**
			 * @param event { import('../../framework/payment/payment-types').PaymentProcessorAuthenticationCompleteEvent }
			 */
			authenticationComplete(event) {
				if (event.token) {
					// Update the payment token on the entry
					this.entry.Entry.PaymentToken.update({
						Status: event.status,
						Type: event.type,
						Token: event.token,
						ClientSecret: event.clientSecret,
						VerificationToken: event.verificationToken
					});
				}

				// Only the success flag (and optional resubmit) is needed beyond this point
				this.$emit('authentication-complete', { success: event.success, forceResubmit: event.forceResubmit });
			}
		}
	};
</script>
